import React, { useState } from "react";
import { api } from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { Loader2, BookOpen, ArrowRight, Trophy } from "lucide-react";
import { useNav, VIEWS } from "@/lib/nav";

/**
 * Clickable rulebook keyword.
 *
 *   - If the keyword resolves to a canonical CARD rule -> jump directly to RuleDetail.
 *   - If exactly one section (rulebook OR steamroller) mentions it -> jump directly.
 *   - If multiple sections mention it -> open a popover with two groups
 *     (Main Rulebook / Steamroller) letting the user pick which to open.
 *
 * Lookup is deferred until first click. The api.js layer dedupes lookups
 * across chips for the same term.
 */
export function KeywordChip({ term, className = "", inline = false, testId }) {
  const { navigate } = useNav();
  const [open, setOpen] = useState(false);
  const [resolved, setResolved] = useState(null);
  const [loading, setLoading] = useState(false);

  const baseClass = inline
    ? "inline px-1 -mx-0.5 rounded text-cyan-300 border border-cyan-700/50 bg-cyan-950/30 hover:bg-cyan-900/40 hover:text-cyan-200 transition leading-tight cursor-pointer"
    : "text-xs px-2 py-0.5 rounded border border-cyan-700/40 bg-cyan-950/30 text-cyan-300 hover:bg-cyan-900/40 transition";

  const goToRulebookSection = (sectionId) => {
    setOpen(false);
    navigate(VIEWS.rulebookSection(sectionId));
  };

  const goToSteamrollerSection = (sectionId) => {
    setOpen(false);
    navigate(VIEWS.steamrollerSection(sectionId));
  };

  const goToRule = (ruleId) => {
    setOpen(false);
    navigate(VIEWS.rule(ruleId));
  };

  const handleClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    let res = resolved;
    if (!res) {
      setLoading(true);
      try {
        res = await api.lookupKeyword(term);
        setResolved(res);
      } catch (err) {
        setLoading(false);
        return;
      }
      setLoading(false);
    }
    // 1) Canonical card rule wins
    if (res.rule_id) { goToRule(res.rule_id); return; }
    const rb = res.rulebook_section_matches || [];
    const sr = res.steamroller_section_matches || [];
    const total = rb.length + sr.length;
    // 2) Exactly one section across both sources -> direct nav
    if (total === 1) {
      if (rb.length === 1) goToRulebookSection(rb[0].section_id);
      else goToSteamrollerSection(sr[0].section_id);
      return;
    }
    // 3) Multiple sections -> show picker
    if (total > 1) { setOpen(true); return; }
    // 4) Nothing matched -> nothing to do
  };

  const rbMatches = resolved?.rulebook_section_matches || [];
  const srMatches = resolved?.steamroller_section_matches || [];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={handleClick}
          className={`${baseClass} ${className}`}
          data-testid={testId || `keyword-chip-${term.toLowerCase().replace(/\s+/g, '-')}`}
          aria-haspopup="dialog"
        >
          {loading ? (
            <Loader2 className="w-3 h-3 inline animate-spin -mt-0.5" />
          ) : (
            term
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-2" align="start" data-testid="keyword-picker">
        <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1.5 px-1.5">
          “{resolved?.name || term}” appears in multiple sections:
        </div>
        <div className="max-h-[60vh] overflow-y-auto pr-1">

        {rbMatches.length > 0 && (
          <div className="mb-2">
            <div className="flex items-center gap-1.5 px-1.5 py-1 text-[10px] uppercase tracking-wider text-cyan-400/80">
              <BookOpen className="w-3 h-3" /> Main Rulebook
              <Badge variant="outline" className="ml-auto text-[10px] border-cyan-800/60 text-cyan-400">
                {rbMatches.length}
              </Badge>
            </div>
            <div className="space-y-0.5">
              {rbMatches.map((m) => (
                <button
                  key={`rb-${m.section_id}`}
                  type="button"
                  onClick={() => goToRulebookSection(m.section_id)}
                  className="w-full text-left px-2 py-1.5 rounded hover:bg-card/80 border border-transparent hover:border-cyan-700/60 group flex items-center gap-2"
                  data-testid={`keyword-picker-${m.section_id}`}
                >
                  <BookOpen className="w-3.5 h-3.5 text-cyan-400 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm truncate">{m.section_title}</p>
                    <p className="text-[10px] text-muted-foreground truncate">{m.section_chapter}</p>
                  </div>
                  <Badge variant="outline" className="text-[10px] shrink-0">{m.match_count}×</Badge>
                  <ArrowRight className="w-3 h-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition" />
                </button>
              ))}
            </div>
          </div>
        )}

        {srMatches.length > 0 && (
          <div>
            <div className="flex items-center gap-1.5 px-1.5 py-1 text-[10px] uppercase tracking-wider text-amber-400/80">
              <Trophy className="w-3 h-3" /> Steamroller (Organized Play)
              <Badge variant="outline" className="ml-auto text-[10px] border-amber-800/60 text-amber-400">
                {srMatches.length}
              </Badge>
            </div>
            <div className="space-y-0.5">
              {srMatches.map((m) => (
                <button
                  key={`sr-${m.section_id}`}
                  type="button"
                  onClick={() => goToSteamrollerSection(m.section_id)}
                  className="w-full text-left px-2 py-1.5 rounded hover:bg-card/80 border border-transparent hover:border-amber-700/60 group flex items-center gap-2"
                  data-testid={`keyword-picker-sr-${m.section_id}`}
                >
                  <Trophy className="w-3.5 h-3.5 text-amber-400 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm truncate">{m.section_title}</p>
                    <p className="text-[10px] text-muted-foreground truncate">{m.section_chapter}</p>
                  </div>
                  <Badge variant="outline" className="text-[10px] shrink-0">{m.match_count}×</Badge>
                  <ArrowRight className="w-3 h-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition" />
                </button>
              ))}
            </div>
          </div>
        )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
